<template>
  <page layoutClass="projects-edit" :loading="$apollo.loading" :err="err">
    <b-row>
      <b-col col lg="8" offset-lg="2">
        <b-card>

          <b-row class="pb-3 mb-3 border-bottom">
            <b-col cols="12" class="pb-2 block-title">{{ isNew ? 'New Project' : 'Edit Project' }}</b-col>
            <b-col cols="12">
              <router-link :to="{ name: 'ProjectShow', params: { projectID } }">&laquo; Back to project</router-link>
            </b-col>
          </b-row>

          <b-form autocomplete="off" method="POST" @submit.prevent="() => isNew ? createProject() : updateProject()">

            <b-form-group label="Title" label-for="formTitle">
              <b-form-input id="formTitle" type="text" required placeholder="Title" v-model="form.title" />
            </b-form-group>

            <b-form-group label="Description" label-for="formDescription">
              <markdown-editor :configs="markdownConfig" id="formDescription" placeholder="Description" v-model="form.description"/>
            </b-form-group>

            <b-row>
              <b-col>
                <b-form-group
                  label="Default Start Date"
                  description="This will be set as default start date of the roles in this project."
                >
                  <date-picker
                    v-model="form.startDate"
                    type="day"
                    lang="en"
                    format="DD MMM YYYY"
                    confirm
                  />
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  label="Dafult End Date"
                  description="This will be set as default estimated end date of the roles in this project."
                >
                  <date-picker
                    v-model="form.endDate"
                    type="day"
                    lang="en"
                    format="DD MMM YYYY"
                    confirm
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group label="Department">
              <b-form-select :options="departmentsList" v-model="form.department"/>
            </b-form-group>

            <b-form-group label="Location">
              <location-search id="location" v-model="form.location"/>
            </b-form-group>

            <!-- b-form-group label="Applicant Tracking System">
              <b-row>
                <b-col cols="12" sm="3" md="4">
                  <b-form-group label="ATS ID" label-for="atsID">
                    <b-form-input disabled id="atsID" type="text" v-model="form.atsID"/>
                    <b-form-text>This is the project ID in your ATS</b-form-text>
                  </b-form-group>
                </b-col>
                <b-col cols="12" sm="9" md="8">
                  <b-form-group label="ATS URL" label-for="atsURL">
                    <b-form-input id="atsURL" type="url" placeholder="https://" v-model="form.atsURL"/>
                    <b-form-text>Enter a URL that redirects candidates to the ATS when they arrive at a share page</b-form-text>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form-group -->

            <!-- b-form-group label="Recruiter Email" label-for="recruiterEmail">
              <b-form-input id="recruiterEmail" type="email" v-model="form.recruiterEmail"/>
              <b-form-text>Enter an email address here to receive a notification when a candidate applies for this role on our site</b-form-text>
            </b-form-group -->

            <b-form-group label="Status">
              <b-form-select :options="statusList" v-model="form.status"/>
            </b-form-group>

            <b-button v-if="isNew" type="submit" variant="primary">Create Project</b-button>
            <b-button v-else type="submit" variant="primary">Save Changes</b-button>

          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </page>
</template>

<script>
import gql from 'graphql-tag';
import ProjectDetailsFragment from '@/graphql/fragments/ProjectDetails.gql';
import Multiselect from 'vue-multiselect';
import { markdownEditor } from 'vue-simplemde';
import Page from '@/components/Page';
import LocationSearch from '@/components/misc/LocationSearch';
import DatePicker from 'vue2-datepicker';

const EditProjectQuery = gql`
  ${ProjectDetailsFragment}

  query ProjectEdit($projectID: ID) {
    company {
      id

      project(where: { id: $projectID }) {
        ...ProjectDetails
      }

      departments {
        id
        name
        icon
      }
    }
  }
`;

const NewProjectMutation = gql`
  ${ProjectDetailsFragment}

  mutation ProjectNew($create: CreateProjectInput!) {
    createProject(create: $create) {
      ...ProjectDetails
    }
  }
`;

const EditProjectMutation = gql`
  ${ProjectDetailsFragment}

  mutation ProjectEdit($projectID: ID!, $update: UpdateProjectInput!) {
    updateProject(update: $update, where: { id: $projectID }) {
      ...ProjectDetails
    }
  }
`;

export default {
  props: {
    projectID: String,
  },
  components: {
    markdownEditor,
    Multiselect,
    Page,
    LocationSearch,
    DatePicker,
  },
  data: () => ({
    err: null,
    form: {
      title: null,
      description: null,
      department: null,
      startDate: null,
      endDate: null,
      location: null,
      atsID: null,
      atsURL: null,
      status: 'ACTIVE',
    },
    departmentsList: [],
    locationsList: [],
  }),
  apollo: {
    project: {
      query: EditProjectQuery,
      variables() {
        return this.isNew ? null : { projectID: this.projectID };
      },
      fetchPolicy: 'network-only',
      update: ({ company: { project } }) => project,
      result({ loading, data }) {
        if (!loading) {
          const { company } = data;

          if (company.project) {
            this.setFormData();
          }

          if (company.departments) {
            this.departmentsList = company.departments.map(({ id, name }) => ({ text: name, value: id }));
          }
        }
      },
      error(err) {
        this.err = `${err}`;
      },
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? 'New Project' : 'Edit Project',
    };
  },
  computed: {
    isNew() {
      return !this.projectID;
    },
    markdownConfig: () => ({
      // autoDownloadFontAwesome: false,
      forceSync: true,
      indentWithTabs: false,
      renderingConfig: { codeSyntaxHighlighting: false },
      promptURLs: true,
      spellChecker: false,
      status: false,
      toolbar: [ 'bold', 'italic', '|', 'link', 'horizontal-rule' ],
    }),
    multiselectConfig: () => ({
      'class': 'multiselect-rlnx',
      'placeholder': 'Type to search',
      'max-height': 600,
      'open-direction': 'bottom',
      'searchable': true,
      'show-no-results': false,
      'taggable': true,
      'tag-position': 'bottom',
      'tag-placeholder': 'Press enter to select',
      'close-on-select': true,
    }),
    statusList: () => ({
      'ACTIVE': 'Active',
      'INACTIVE': 'Inactive',
      'DELETED': 'Deleted',
    }),
  },
  methods: {
    getFormData() {
      const data = {};

      Object.keys(this.form).forEach(prop => {
        if ([ 'department', 'location' ].includes(prop)) {
          data[prop] = this.form[prop] && this.form[prop].id ? { id: this.form[prop].id } : null;
        } else {
          data[prop] = this.form[prop];
        }
      });

      if (!data.status) {
        data.status = 'INACTIVE';
      }

      return data;
    },
    setFormData() {
      Object.keys(this.form).forEach(this.project ? prop => {
        if (prop === 'department') {
          this.form[prop] = this.project[prop] && this.project[prop].id ? this.project[prop].id : null;
        } else if (prop === 'companyName' || prop === 'recruiterEmail') {
          this.form[prop] = Array.isArray(this.project.customFields)
            ? (this.project.customFields.find(({ key }) => key === prop) || {}).value
            : null;
        } else {
          this.form[prop] = this.project[prop];
        }
      } : prop => this.form[prop] = null);
    },
    async createProject() {
      try {
        this.$assert(this.isNew, new Error('Cannot createProject with updateProject params'));

        const create = this.getFormData();
        this.$log.debug({ create });

        const { data: { createProject: { id: projectID } = {} } = {} } = await this.$apollo.mutate({
          mutation: NewProjectMutation,
          variables: { create },
        });

        this.$assert(projectID, new Error('Expected createProject to return a projectID'));
        this.$router.replace({ name: 'ProjectShow', params: { projectID } });
      } catch (err) {
        this.$log.error(err);
        this.err = `${err}`;
      }
    },
    async updateProject() {
      try {
        this.$assert(!this.isNew, new Error('Cannot createProject with updateProject params'));

        const update = this.getFormData();
        this.$log.debug({ update });

        await this.$apollo.mutate({
          mutation: EditProjectMutation,
          variables: { projectID: this.projectID, update },
        });

        this.setFormData();
        this.$router.replace({ name: 'ProjectShow', params: { projectID: this.projectID } });
      } catch (err) {
        this.$log.error(err);
        this.err = `${err}`;
      }
    },
  },
};
</script>

<style lang="scss">
.page .projects-edit {

  .markdown-editor .CodeMirror {
    height: 300px;

    .CodeMirror-lines {
      padding-bottom: 20px;
    }
  }

  .block-title {
    text-transform: uppercase;
  }

}
</style>



// WEBPACK FOOTER //
// src/views/projects/ProjectEdit.vue